import { css } from 'aphrodite';
import * as React from 'react';
import { IAuditLogEntry, AuditLogCategory } from '../../../../../queries/Admin/AuditLog/types';
import { ChevronDown } from '../../../../../web/components/svgs/icons/ChevronDown';
import { Avatar } from '@HoudiniUI/Avatar';
import { Button } from '../../../../../web/components-v2/Button';
import { styleSheet } from './styles';

interface Props {
	entry: IAuditLogEntry;
}

function categoryPill(entry: IAuditLogEntry): { color: 'green' | 'blue' | 'gray'; label: string } {
	switch (entry.category) {
		case AuditLogCategory.TagChanges:
			return { color: 'green', label: 'Tag change' };
		case AuditLogCategory.ContactMutation:
			return { color: 'blue', label: 'Contact mutation' };
		default:
			return { color: 'gray', label: entry.eventType };
	}
}

function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/);
	return parts
		.slice(0, 2)
		.map(p => p[0]?.toUpperCase() ?? '')
		.join('');
}

const COLOR_STYLE_MAP = {
	green: { pill: styleSheet.actionPillGreen, dot: styleSheet.actionDotGreen },
	blue: { pill: styleSheet.actionPillBlue, dot: styleSheet.actionDotBlue },
	gray: { pill: styleSheet.actionPillGray, dot: styleSheet.actionDotGray },
} as const;

export const EventLogRow: React.FC<Props> = ({ entry }) => {
	const [expanded, setExpanded] = React.useState(false);
	const hasDetails = !!entry.details && Object.keys(entry.details).length > 0;
	// Ties the expand control to the panel it reveals for assistive tech (aria-controls).
	const detailsId = `event-log-details-${entry.id}`;

	const toggle = () => setExpanded(prev => !prev);

	const pill = categoryPill(entry);
	const { pill: pillStyle, dot: dotStyle } = COLOR_STYLE_MAP[pill.color];
	const initials = getInitials(entry.actor.name || 'Unknown');

	return (
		<>
			<tr
				className={css(
					styleSheet.row,
					hasDetails ? styleSheet.rowInteractive : null,
					expanded ? styleSheet.rowExpanded : null
				)}
				// Whole-row click is a mouse convenience only; the chevron button is the
				// keyboard/assistive-tech affordance. Rows without details are inert.
				onClick={hasDetails ? toggle : undefined}
			>
				<td className={css(styleSheet.cell, styleSheet.truncatedCell, styleSheet.firstColumn)}>
					{entry.timestampDisplay}
				</td>
				<td className={css(styleSheet.cell, styleSheet.truncatedCell)}>
					<span className={css(styleSheet.actorCell)}>
						<Avatar fallbackText={initials} styles={[styleSheet.avatar]} />
						<span className={css(styleSheet.actorName)}>{entry.actor.name || 'Unknown user'}</span>
						{entry.actor.isLevitateSupport ? <span className={css(styleSheet.levitateBadge)}>Levitate</span> : null}
					</span>
				</td>
				<td className={css(styleSheet.cell, styleSheet.truncatedCell)}>
					<span className={css(styleSheet.actionPill, pillStyle)}>
						<span className={css(styleSheet.actionDot, dotStyle)} />
						{pill.label}
					</span>
				</td>
				<td className={css(styleSheet.cell, styleSheet.truncatedCell)}>{entry.summary}</td>
				<td className={css(styleSheet.cell, styleSheet.chevronCell, styleSheet.lastColumn)}>
					{hasDetails ? (
						<Button
							variant='ghost'
							size='icon'
							styles={[styleSheet.expandButton]}
							aria-expanded={expanded}
							aria-controls={detailsId}
							aria-label={expanded ? 'Hide event details' : 'Show event details'}
							onClick={e => {
								// Prevent the row's onClick from also firing and double-toggling.
								e.stopPropagation();
								toggle();
							}}
						>
							<ChevronDown className={css(styleSheet.expandChevron, expanded ? styleSheet.expandChevronOpen : null)} />
						</Button>
					) : null}
				</td>
			</tr>
			{expanded && hasDetails ? (
				// Stop clicks inside the details from bubbling to the row's onClick and collapsing the panel.
				<tr id={detailsId} className={css(styleSheet.detailsRow)} onClick={e => e.stopPropagation()}>
					<td className={css(styleSheet.cell)} colSpan={5}>
						<div className={css(styleSheet.detailsDrawer)}>
							<div className={css(styleSheet.detailsGroup)}>
								<span className={css(styleSheet.detailsGroupLabel)}>Changed fields</span>
								<span className={css(styleSheet.detailsChips)}>
									{Object.keys(entry.details).map(key => (
										<span key={key} className={css(styleSheet.fieldChip)}>
											{key}
										</span>
									))}
								</span>
							</div>
							<div className={css(styleSheet.detailsMeta)}>
								<span className={css(styleSheet.detailsMetaItem)}>
									<span className={css(styleSheet.detailsMetaLabel)}>Type</span>
									<span className={css(styleSheet.detailsMetaValue)}>{entry.eventType}</span>
								</span>
								<span className={css(styleSheet.detailsMetaItem)}>
									<span className={css(styleSheet.detailsMetaLabel)}>Event ID</span>
									<span className={css(styleSheet.detailsMetaValue)}>{entry.id}</span>
								</span>
							</div>
						</div>
					</td>
				</tr>
			) : null}
		</>
	);
};
